<script setup lang="ts">
import { inject, ref, watch, useId, Reactive } from 'vue';
import IconTooltip from '@components/icons/icon-tooltip.vue';
import Icon from '@components/icons/icon.vue';
import Button from '@components/buttons/button.vue';
import { DisabledPropsInterface, useDisableComposable } from '@src/application/composables/disabled-composable';
import type { IconType } from '@components/icons/icon-types.ts';

const {
    activeTitle = true,
    context = 'default',
    bordersTitle = true,
    bordersContent = true,
    buttonPosition = 'right',
    iconShowContent = 'chevron-up',
    iconHideContent = 'chevron-down',
    showOpened,
} = defineProps<DisabledPropsInterface & {
    title?: string|null,
    activeTitle?: boolean,
    context?: 'default'| 'solid'| 'light'| 'raw'
    bordersTitle?: boolean,
    bordersContent?: boolean,
    buttonPosition?: 'right'| 'left'
    iconShowContent?: IconType
    iconHideContent?: IconType
    highlightOpened?: boolean
    showOpened?: boolean
    color?: 'red'|'blue'|'yellow'|'green'|'orange'
}>();

const id = useId();

const expandables = inject<Reactive<{ [vueId: string]: boolean }>|null>('expandables', null);

if (expandables) {
    expandables[id] = false;
}

const showContent = ref<boolean>(showOpened);

watch(() => expandables, () => {
    if (expandables === null) {
        return;
    }
    showContent.value = expandables[id];
}, {
    deep: true,
});

const emits = defineEmits<
    ((event: 'show-content', payload: {
        showContent: boolean,
        uid: string,
    }) => void)
>();

watch(showContent, () => {
    if (expandables !== null) {
        expandables[id] = showContent.value;
    }
    emits('show-content', {
        showContent: showContent.value,
        uid: id,
    });
});

const { disabled } = useDisableComposable();

const sideBar = inject<string|null>('sideBar', null);
</script>

<template>
    <div
        v-show="sideBar === null || showContent"
        :class="[
            $style.expandable__wrapper,
            sideBar !== null ? $style.sidebar : '',
            color ? [$style.color, $style[color]] : '',
        ]"
    >
        <div
            :class="[
                $style.expandable_header,
                $style['button_' + buttonPosition],
                $style[context],
                activeTitle && !disabled ? $style.active_title : '',
                showContent ? $style.show_content : '',
                highlightOpened ? $style.highlight_opened : '',
                bordersTitle ? $style.borders : '',
                activeTitle && disabled ? $style.disabled_title : '',
            ]"
            @click="activeTitle && !disabled ? showContent = !showContent : null"
        >
            <p v-if="title" :class="[$style.title, $style.title_prop]">{{ title }}</p>
            <div v-else :class="[$style.title, $style.title_slot]">
                <slot name="title"/>
            </div>
            <Button
                :class="$style.expandable_trigger"
                :context="context === 'default' ? 'primary' : 'default'"
                @click="!activeTitle ? showContent = !showContent : null"
            >
                <IconTooltip
                    v-if="sideBar !== null"
                    position="left"
                    context="primary"
                    tooltip="Ausblenden"
                    :icon="iconHideContent"
                />
                <Icon
                    v-else
                    :icon="showContent ? iconHideContent : iconShowContent"
                    :icon-color="context === 'default' ? 'white' : 'default'"
                />
            </Button>
            <teleport v-if="sideBar !== null" :to="sideBar">
                <Button :class="$style.expandable_trigger" @click="showContent = !showContent">
                    <slot name="side-bar-button"/>
                </Button>
            </teleport>
        </div>
        <div v-show="showContent" :class="[$style.expandable_content, bordersContent ? $style.borders : '']">
            <slot/>
        </div>
    </div>
</template>

<style lang="scss" module>
@use '@text' as *;
@use '@borders' as *;
@use '@backgrounds' as *;

.expandable__wrapper {
    margin-bottom: .3em;
    background-color: #fff;
    position: relative;

    &.sidebar,
    &:last-child {
        margin-bottom: initial;
    }
    &.sidebar .expandable_header {
        position: sticky;
        top: 0;
        z-index: 1;
        background-color: #fff;
    }
    &.color {
        &::after {
            position: absolute;
            top: 0;
            left: 0;
            width: 4px;
            height: 100%;
            content: '';
        }

        &.red::after {
            background-color: $red-background;
        }
        &.blue::after {
            background-color: $blue-background;
        }
        &.yellow::after {
            background-color: $yellow-background;
        }
        &.green::after {
            background-color: $green-background;
        }
        &.orange::after {
            background-color: $orange-background;
        }
    }
}
.expandable_header {
    display: flex;

    &.disabled_title {
        cursor: not-allowed;
    }
    .title {
        flex-grow: 1;
        padding-left: .7em;
    }
    .title_prop {
        display: inline;
        padding: .7rem;
        font-weight: $weight-medium;
        vertical-align: middle;
    }
    .title_slot {
        display: flex;
        align-items: center;
    }
    &.button_left {
        flex-direction: row-reverse;
    }
    &.active_title > .title {
        cursor: pointer;

        * { cursor: pointer; }
    }
    .expandable_trigger {
        min-width: 2.5em;
        padding: 0;
        border-radius: 0;
    }
}
.expandable_content {
    padding: .7em;

    &.borders {
        border: $thin-line-grey;
        border-top: none;
    }
}

// Bei geschachteten Expandables können sich die Klassen der Eltern-Kontexte auch auf die Kinder auswirken.
// Deswegen css der Kontexte in Abhängigkeit zu Kindern ( > ) und Geschwistern ( ~ / + ).

$highlight-grey: #e6e6e6;
$solid-grey: #f0f0f0;

.default {
    &.borders > .title {
        border: $thin-line-grey;
    }
    &.button_left > .title {
        border-left: none;
    }
    &.show_content.highlight_opened {
        background-color: $highlight-grey;
        > .title {
            border-color: $highlight-grey;
        }
    }
}
.light, .raw, .solid {
    &.button_left > .title {
        padding-left: .25em;
    }
}
.light.show_content {
    &::after {
        position: absolute;
        left: 0;
        width: 4px;
        height: 100%;
        content: '';
        background-color: $primary-background;
    }
}
.light, .raw {
    position: relative;

    &.borders {
        border: $thin-line-grey;
    }
    &.show_content {
        &.highlight_opened {
            background-color: $highlight-grey;
            border-color: $highlight-grey;
        }
    }
    > .expandable_trigger {
        color: $default-text-color;
        background-color: transparent;
        border: none;
    }
}
.solid {
    &.expandable_header,
    > .expandable_trigger {
        background-color: $solid-grey;
    }
    &.highlight_opened {
        &.show_content,
        &.show_content > .expandable_trigger {
            background-color: $highlight-grey;
        }
    }
    > .expandable_trigger {
        color: $default-text-color;
        border: none;
    }
}
</style>
